import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { createGameState } from '@/engine/state/createGameState';
import {
  dispatch, rollAction, takeLegTileAction, placeDesertAction, betAction,
} from '@/engine/actions/turnActions';
import { decideAction, personalityFor, PERSONALITIES } from '@/engine/ai/heuristicBot';
import { createRngState } from '@/engine/rng/rng';
import { PHASES } from '@/engine/constants';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { rollAnimationMs } from '@/utils/rollTheatre';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, commitAction, setTableSize,
  sweepMyRooms, recordWaiting,
  shouldAutoPlay, personalityForSeat, seqOf, DISCONNECT_GRACE_MS,
} from '@/store/multiplayer';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';

// The single place engine `dispatch()` is called from, same convention as
// the sibling repos. Components never touch engine state directly.

const SAVE_KEY = 'camel-up:saved-game';
const SETTINGS_KEY = 'camel-up:settings';

// A bot turn here is exactly ONE action, so unlike cant-stop there is no
// long chain to keep watchable - each pause is a read beat on its own.
// Rolls owe the table their own die animation on top (rollAnimationMs),
// and a roll that ENDED a leg gets the long beat: the scoring panel is on
// screen and people are reading who won what.
export const BOT_PAUSE_MS = 1000;
export const LEG_SCORED_PAUSE_MS = 3200;
export const BOT_PAUSE_JITTER = 0.3;

export const botPause = (base, random = Math.random) => (
  Math.round(base * (1 + (random() * 2 - 1) * BOT_PAUSE_JITTER))
);

/**
 * The pause before the NEXT bot action, computed from what just happened:
 * the standing read beat, plus the die animation if the last event was a
 * roll, plus the long scoring beat if that roll closed a leg.
 */
export const pauseForNextAction = (game, random = Math.random) => {
  const lastLog = game?.log?.[game.log.length - 1];
  let pause = botPause(BOT_PAUSE_MS, random) + rollAnimationMs(game);
  if (lastLog?.kind === 'roll' && game.diceRolled.length === 0) {
    pause += LEG_SCORED_PAUSE_MS;
  }
  return pause;
};

/**
 * The bots' randomness, derived from the game itself: seed and seq. Every
 * client (and every transaction retry) that asks "what would this bot do
 * HERE" gets the same answer, and a replayed save replays its bots.
 */
export const botRngFor = (game) => createRngState(
  (game.seed ^ ((game.meta.seq + 1) * 1000003)) | 0,
);

let botTimer = null;
let unbindRoomFn = null;
let unwatchStatusFn = null;
const lastSeen = {};

const cancelBotTurn = () => {
  if (botTimer !== null) {
    window.clearTimeout(botTimer);
    botTimer = null;
  }
};

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must never break play.
  }
};

export default createStore({
  state: {
    game: null,
    thinkingPlayerId: null,
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You', opponents: 3 },
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),

    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    roomId: null,
    roomMeta: null,
    presence: {},
    roomError: null,
  },

  getters: {
    inPlay: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.PLAYING,
    isFinished: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.FINISHED,
    activePlayer: (state) => (state.game
      ? state.game.players[state.game.turn.activeSeat]
      : null),
    humanPlayer: (state) => state.game?.players.find((p) => !p.isBot) || null,
    isHumanTurn: (state, getters) => Boolean(
      getters.inPlay && getters.activePlayer && !getters.activePlayer.isBot && !state.thinkingPlayerId,
    ),
    isOnline: (state) => Boolean(state.roomId),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,

    // Me, in both modes: online, player ids ARE Firebase uids; locally the
    // one non-bot seat. Same getter, same wording, in every sibling game.
    me: (state) => {
      if (!state.game) return null;
      if (state.roomId) return state.game.players.find((p) => p.id === state.user?.uid) || null;
      return state.game.players.find((p) => !p.isBot) || null;
    },
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isMyOnlineTurn: (state) => {
      if (!state.roomId || !state.game) return false;
      const active = state.game.players[state.game.turn.activeSeat];
      return Boolean(active && !active.isBot && active.id === state.user?.uid);
    },
    // The seat whose input the UI should offer right now - local human turn,
    // or my own online turn.
    myTurn: (state, getters) => (state.roomId ? getters.isMyOnlineTurn : getters.isHumanTurn),
    mySeat: (state, getters) => {
      if (!state.game) return -1;
      if (state.roomId) return state.game.players.findIndex((p) => p.id === state.user?.uid);
      return getters.humanPlayer?.seat ?? -1;
    },
    absentPlayerIds: (state) => {
      if (!state.roomId || !state.game) return [];
      return state.game.players
        .filter((p) => !p.isBot && !state.presence[p.id])
        .map((p) => p.id);
    },
  },

  mutations: {
    setGame(state, game) {
      state.game = game;
      state.hasSavedGame = Boolean(game);
      saveJson(SAVE_KEY, game);
    },
    setThinking(state, playerId) {
      state.thinkingPlayerId = playerId;
    },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },
    clearGame(state) {
      state.game = null;
      state.thinkingPlayerId = null;
      state.hasSavedGame = false;
      saveJson(SAVE_KEY, null);
    },
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setRoom: (state, { id, meta }) => { state.roomId = id; state.roomMeta = meta ?? null; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
    // Online state arrives from the listener - never written to the local
    // save slot (that slot belongs to the local game only).
    setRemoteGame(state, game) { state.game = game; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        game: state.game,
        myId: getters.me?.id,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ game: state.game, roomId: state.roomId }),
        outcome,
      });
    },

    newGame({ commit, dispatch: dispatchAction, state }, { playerName, opponents, seed } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      const botCount = opponents || state.settings.opponents || 3;

      const players = [
        { id: 'you', name, isBot: false },
        ...Array.from({ length: botCount }, (_, i) => ({
          id: `bot${i}`,
          name: PERSONALITIES[personalityFor(i)].label,
          isBot: true,
          botIndex: i,
        })),
      ];

      cancelBotTurn();
      commit('setSettings', { playerName: name, opponents: botCount });
      const game = createGameState({ players, seed: seed ?? Date.now() });
      game.players = game.players.map((p, i) => (
        players[i].isBot ? { ...p, botIndex: players[i].botIndex } : p
      ));
      commit('setGame', game);
      dispatchAction('runBotsIfNeeded');
    },

    resumeGame({ commit, dispatch: dispatchAction }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      cancelBotTurn();
      commit('setGame', saved);
      dispatchAction('runBotsIfNeeded');
    },

    /**
     * A human's move, local or online - all four game actions funnel
     * through here so the routing decision lives in one place.
     */
    play({ commit, dispatch: dispatchAction, state, getters }, action) {
      if (state.roomId) { dispatchAction('playOnline', action); return; }
      if (!getters.isHumanTurn) return;
      commit('setGame', dispatch(state.game, action));
      dispatchAction('runBotsIfNeeded');
    },

    roll({ dispatch: dispatchAction }) { dispatchAction('play', rollAction()); },
    takeLegTile({ dispatch: dispatchAction }, camel) { dispatchAction('play', takeLegTileAction(camel)); },
    placeDesert({ dispatch: dispatchAction }, { space, side }) { dispatchAction('play', placeDesertAction(space, side)); },
    bet({ dispatch: dispatchAction }, { pile, camel }) { dispatchAction('play', betAction(pile, camel)); },

    /**
     * Plays consecutive bot turns, one at a time, paced by what just
     * happened on the table. One timer at a time; the decision is
     * recomputed when it fires - the house rules.
     */
    runBotsIfNeeded({ commit, dispatch: dispatchAction, state }) {
      if (botTimer !== null) return;
      if (!state.game || state.game.meta.phase !== PHASES.PLAYING) {
        commit('setThinking', null);
        return;
      }
      const active = state.game.players[state.game.turn.activeSeat];
      if (!active?.isBot) {
        commit('setThinking', null);
        return;
      }

      commit('setThinking', active.id);
      const actingId = active.id;
      const pause = pauseForNextAction(state.game);

      botTimer = window.setTimeout(() => {
        botTimer = null;
        if (!state.game || state.game.meta.phase !== PHASES.PLAYING) {
          commit('setThinking', null);
          return;
        }
        const current = state.game.players[state.game.turn.activeSeat];
        if (!current?.isBot || current.id !== actingId) {
          commit('setThinking', null);
          dispatchAction('runBotsIfNeeded');
          return;
        }
        const { action } = decideAction(
          state.game, current.seat, botRngFor(state.game), personalityForSeat(current),
        );
        commit('setGame', dispatch(state.game, action));
        commit('setThinking', null);
        dispatchAction('runBotsIfNeeded');
      }, pause);
    },

    // ---------------------------------------------------------------- auth

    initAuth({ commit, dispatch: dispatchAction }) {
      if (!firebaseConfigured || !auth) {
        commit('setAuthReady', true);
        return;
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? {
          uid: user.uid, displayName: user.displayName, email: user.email,
        } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
          if (user.displayName) dispatchAction('rememberName', user.displayName);
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    rememberName({ commit, state }, name) {
      if (!name || state.settings.playerName === name) return;
      commit('setSettings', { playerName: name });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch: dispatchAction }) {
      await dispatchAction('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ------------------------------------------------------------ rooms

    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch: dispatchAction }, { tableSize, async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({
          user: state.user,
          tableSize: tableSize ?? (state.settings.opponents || 3) + 1,
          async: asyncRoom,
        });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async joinRoomByCode({ commit, state, dispatch: dispatchAction }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    enterRoom({ commit, state, dispatch: dispatchAction }, gameId) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRemoteGame', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => {
          commit('setRoomMeta', meta);
          if (meta === null) dispatchAction('leaveRoom');
        },
        onState: (game) => {
          commit('setRemoteGame', game);
          dispatchAction('driveOnlineTurn');
        },
        onPresence: (presence) => {
          const now = Date.now();
          Object.keys(lastSeen).forEach((uid) => { if (presence[uid]) delete lastSeen[uid]; });
          (state.game?.players || []).forEach((p) => {
            if (!p.isBot && !presence[p.id] && !lastSeen[p.id]) lastSeen[p.id] = now;
          });
          commit('setPresence', presence);
          dispatchAction('driveOnlineTurn');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setRemoteGame', null);
      commit('setThinking', null);
    },

    async changeTableSize({ commit, state }, tableSize) {
      try {
        await setTableSize({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, tableSize,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async dealRoom({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // ------------------------------------------------- the online turn loop

    driveOnlineTurn({ state, dispatch: dispatchAction, commit }) {
      if (!state.roomId || !state.game || botTimer !== null) return;
      if (state.game.meta.phase !== PHASES.PLAYING) { commit('setThinking', null); return; }

      const mine = shouldAutoPlay({
        gameState: state.game,
        presence: state.presence,
        myUid: state.user?.uid,
        disconnectedSince: lastSeen,
        asyncRoom: Boolean(state.roomMeta?.async),
      });
      if (!mine) {
        const active = state.game.players[state.game.turn.activeSeat];
        if (active && !active.isBot && !state.presence[active.id] && lastSeen[active.id]) {
          const waited = Date.now() - lastSeen[active.id];
          if (waited < DISCONNECT_GRACE_MS) {
            botTimer = window.setTimeout(() => {
              botTimer = null;
              dispatchAction('driveOnlineTurn');
            }, DISCONNECT_GRACE_MS - waited + 100);
          }
        }
        commit('setThinking', null);
        return;
      }

      const active = state.game.players[state.game.turn.activeSeat];
      commit('setThinking', active.id);
      const pause = pauseForNextAction(state.game);

      botTimer = window.setTimeout(async () => {
        botTimer = null;
        commit('setThinking', null);
        if (!state.roomId || !state.game || state.game.meta.phase !== PHASES.PLAYING) return;
        const expectedSeq = seqOf(state.game);
        try {
          const result = await commitAction({
            gameId: state.roomId,
            expectedSeq,
            // Recomputed against whatever the transaction actually finds;
            // botRngFor keys on (seed, seq), so a retry agrees with itself.
            apply: (current) => {
              const seat = current.players[current.turn.activeSeat];
              if (!seat) return null;
              const { action } = decideAction(
                current, seat.seat, botRngFor(current), personalityForSeat(seat),
              );
              return dispatch(current, action);
            },
          });
          if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
        } catch (error) {
          commit('setRoomError', error.message);
        }
      }, pause);
    },

    async playOnline({ state, commit, getters }, action) {
      if (!getters.isMyOnlineTurn) return;
      const expectedSeq = seqOf(state.game);
      try {
        const { committed, state: committedState } = await commitAction({
          gameId: state.roomId,
          expectedSeq,
          apply: (current) => {
            const seat = current.players[current.turn.activeSeat];
            if (!seat || seat.id !== state.user?.uid) return null;
            return dispatch(current, action);
          },
        });
        if (committed) recordWaiting({ gameId: state.roomId, gameState: committedState, meta: state.roomMeta });
        if (!committed) commit('setRoomError', null);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    abandonGame({ commit }) {
      cancelBotTurn();
      commit('clearGame');
    },
  },
});
